import { BulkUpsertBenefitsSpendingLimitOverrideDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TransactionCategory } from './transactionCategories'

interface SpendingLimitOverride {
  employeeId: string
  category: TransactionCategory
  amountCents: number
  companyId: string
  expiresAt?: string | null
}

export function useBulkUpsertSpendingLimits() {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [bulkUpsertSpendingLimits, { loading }] = useMutation(
    BulkUpsertBenefitsSpendingLimitOverrideDocument,
    {
      extractorFn: (data) => data.benefitsMutations.bulkUpsertBenefitsSpendingLimitOverride,
    }
  )

  const upsertLimits = async (
    overrides: Omit<SpendingLimitOverride, 'companyId'>[],
    onSuccess?: () => void | Promise<void>
  ) => {
    const spendingLimitOverrides = overrides.map((o) => ({ ...o, companyId }))
    const result = await bulkUpsertSpendingLimits({
      variables: { spendingLimitOverrides },
    })
    const handler = handleResult(result, () => undefined)
    if (handler.success) {
      const updatedEmployeesCount = new Set(
        spendingLimitOverrides.map((override) => override.employeeId)
      ).size

      app.setFlash({
        type: 'correct',
        message: i18n.tp(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.success_message',
          {
            count: updatedEmployeesCount,
          }
        ),
      })
      if (onSuccess) await onSuccess()
      return true
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.upsert_limits_error_message'
        ),
      })
      return false
    }
  }

  return { upsertLimits, loading }
}
